<script>
    import Modal from '@/components/modals/Modal';
    import utilsService from '@/services/core/utils.service';
    import sensorsService from '@/services/sensors.service';
    import TinyPumperIncidentsList from '@/components/tinyPumper/TinyPumperIncidentsList';
    import InfoTooltip from '@/components/shared/InfoTooltip';

    export default {
        props: {
            modalData: Object
        },
        components: {
            Modal,
            TinyPumperIncidentsList,
            InfoTooltip
        },
        data() {
            return {
                isLoading: 0,
                statusData: null,
                isLoadingLevelcon: 0,
                levelconData: null
            };
        },
        computed: {
            getCardsInfo() {
                const { numberOfReportsLast24Hours, averageReportsPerDayLast7Days, consecutiveHourConnectionFailuresLast7Days } =
                    this.statusData || {};

                return [
                    {
                        title: 'Reports in the Last 24 Hours',
                        text: `${numberOfReportsLast24Hours || 0} / 24`,
                        colorClass: this.getReportsColorClass(numberOfReportsLast24Hours)
                    },
                    {
                        title: 'Average Reports per Day (Last 7 Days)',
                        text: `${averageReportsPerDayLast7Days || 0} / 24`,
                        colorClass: this.getReportsColorClass(averageReportsPerDayLast7Days)
                    },
                    {
                        title: 'Consecutive Hourly Failures (Last 7 Days)',
                        text: `${consecutiveHourConnectionFailuresLast7Days || 0}`,
                        colorClass:
                            consecutiveHourConnectionFailuresLast7Days === 0
                                ? 'primary'
                                : consecutiveHourConnectionFailuresLast7Days > 5
                                  ? 'danger'
                                  : 'warning'
                    }
                ];
            }
        },
        methods: {
            dateFormat: date => utilsService.dateFormat(date, 'MM/DD/YYYY hh:mm A'),
            loadSensors() {
                this.isLoading++;
                sensorsService
                    .status(this.modalData.sensorId)
                    .then((statusData = {}) => (this.statusData = statusData))
                    .finally(() => this.isLoading--);
            },
            loadLevelcon() {
                this.isLoadingLevelcon++;
                sensorsService
                    .levelcon(this.modalData.sensorId)
                    .then(data => (this.levelconData = data))
                    .finally(() => this.isLoadingLevelcon--);
            },
            getMessageColorClass(message) {
                if (message?.includes('Good')) return 'text-primary';
                else if (message?.includes('Low')) return 'text-warning';
                else if (message?.includes('Asleep') || message?.includes('Dead')) return 'text-danger';
            },
            getReportsColorClass(value) {
                if (value >= 20) return 'primary';
                else if (value > 10 && value < 20) return 'warning';

                return 'danger';
            }
        },
        mounted() {
            this.loadSensors();
            this.loadLevelcon();
        }
    };
</script>
<template>
    <Modal sizeClass="modal-xl">
        <div class="modal-content">
            <div class="modal-header">
                <h3 class="modal-title">{{ modalData.assetName }}</h3>
            </div>
            <div class="modal-body">
                <div v-if="isLoading > 0" class="d-flex justify-content-center align-items-center py-5">
                    <div class="spinner-border text-primary" role="status"></div>
                </div>
                <template v-else>
                    <div class="row g-3">
                        <div v-if="statusData?.batteryHealth?.startPinId != null" class="col-md d-flex flex-column">
                            <h6>Gateway</h6>
                            <div class="bg-light border rounded-1 p-3 flex-grow-1">
                                <div class="d-flex align-items-baseline gap-2">
                                    <b>Id:</b>
                                    <a
                                        :href="`https://one.levelcon.com/F100.html?id=${statusData?.batteryHealth?.startPinId}`"
                                        target="_blank"
                                        class="text-body"
                                    >
                                        {{ statusData?.batteryHealth?.startPinId }}
                                        <i class="bi bi-box-arrow-up-right"></i>
                                    </a>
                                </div>
                                <div class="d-flex align-items-baseline gap-2">
                                    <b>Voltage:</b>
                                    <span>{{ statusData?.batteryHealth?.startPinVoltage }} V</span>
                                </div>
                                <div class="d-flex align-items-baseline gap-2">
                                    <b>Message:</b>
                                    <span :class="getMessageColorClass(statusData?.batteryHealth?.startPinMessage)">
                                        {{ statusData?.batteryHealth?.startPinMessage }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="col-md d-flex flex-column">
                            <h6>Sensor</h6>
                            <div class="bg-light border rounded-1 p-3 flex-grow-1">
                                <div class="d-flex align-items-baseline gap-2">
                                    <b>Type:</b>
                                    <span>{{ statusData?.batteryHealth?.batteryType }}</span>
                                </div>
                                <div class="d-flex align-items-baseline gap-2">
                                    <b>Voltage:</b>
                                    <span>{{ statusData?.batteryHealth?.voltage }} V</span>
                                </div>
                                <div class="d-flex align-items-baseline gap-2">
                                    <b>Message:</b>
                                    <span :class="getMessageColorClass(statusData?.batteryHealth?.message)">
                                        {{ statusData?.batteryHealth?.message }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="col-md d-flex flex-column">
                            <h6>Solar Panel Capacitor</h6>
                            <div class="bg-light border rounded-1 p-3 flex-grow-1">
                                <div class="d-flex align-items-baseline gap-2">
                                    <b>Detected:</b>
                                    <span>{{ statusData?.batteryHealth?.solarPanelCapacitorDetected ? 'Yes' : 'No' }}</span>
                                </div>
                                <template v-if="statusData?.batteryHealth?.solarPanelCapacitorDetected">
                                    <div class="d-flex align-items-baseline gap-2">
                                        <b>Min. Voltage (Last 2 Days):</b>
                                        <span>{{
                                            statusData?.batteryHealth?.minVoltageLevelInLast2Days >= 0
                                                ? `${statusData?.batteryHealth?.minVoltageLevelInLast2Days} V`
                                                : '-'
                                        }}</span>
                                    </div>
                                    <div class="d-flex align-items-baseline gap-2">
                                        <b>Message:</b>
                                        <span v-if="!statusData?.batteryHealth?.potentialBlownOrFaulty" class="text-primary"
                                            >Good Health</span
                                        >
                                        <span v-else class="text-danger">
                                            Capacitor must be replaced
                                            <InfoTooltip
                                                iconClass="fas fa-info-circle text-danger fs-4"
                                                text="This signifies a blown or faulty Capacitor that must be replaced. Located in the place in which you installed it. Solar panel is wiped clean. Solar panel has an unobstructed view of the sky (not the horizon). When reviewing your TinyPumper: Single flash every 8 seconds = healthy. Double flash = low batteries. Triple flash = monitor issue. Please note: flashes only visible on TinyPumper, NOT Sensors."
                                            />
                                        </span>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>

                    <div class="row g-3 mt-2">
                        <div class="col-12">
                            <h6>Levelcon Last Report</h6>
                            <div class="bg-light border rounded-1 p-3">
                                <div v-if="isLoadingLevelcon" class="d-flex align-items-center gap-2 text-muted">
                                    <div class="spinner-border spinner-border-sm" role="status"></div>
                                    <span>Loading...</span>
                                </div>
                                <div v-else-if="levelconData?.dateTimeUtc" class="d-flex flex-column gap-1">
                                    <div class="d-flex align-items-baseline gap-2">
                                        <b>Value:</b>
                                        <span>{{ levelconData.value ?? '—' }}</span>
                                    </div>
                                    <div class="d-flex align-items-baseline gap-2">
                                        <b>Date (UTC):</b>
                                        <span class="font-monospace">{{ dateFormat(levelconData.dateTimeUtc, 'MM/DD/YYYY hh:mm A') }}</span>
                                    </div>
                                </div>
                                <span v-else class="text-muted">No data available</span>
                            </div>
                        </div>
                    </div>

                    <div class="border-top my-4"></div>

                    <div class="row g-3">
                        <div v-for="(card, index) in getCardsInfo" :key="`getCardsInfo-${index}`" class="col-md-4">
                            <div class="alert h-100 d-flex flex-column justify-content-between" :class="`alert-${card.colorClass}`">
                                <b>{{ card.title }}</b>
                                <b class="fs-4">{{ card.text }}</b>
                            </div>
                        </div>
                    </div>

                    <div class="row g-3 mt-1">
                        <div class="col-md-4">
                            <h6>Reports in the Last 24 Hours</h6>
                            <div class="overflow-auto" style="max-height: 24.5rem">
                                <table class="table table-striped mb-0">
                                    <tbody>
                                        <tr
                                            v-for="(report, index) in statusData?.last24HoursReports || []"
                                            :key="`last24HoursReports-${index}`"
                                        >
                                            <td class="font-monospace">{{ dateFormat(report.dateTime, 'MM/DD/YYYY hh:mm A') }}</td>
                                        </tr>
                                        <tr v-if="!statusData?.last24HoursReports?.length">
                                            <td colspan="2" class="text-center fs-4 py-4">Not reported</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div class="col-md">
                            <h6>Average Reports per Day (Last 7 Days)</h6>
                            <div class="overflow-auto" style="max-height: 24.5rem">
                                <table class="table table-striped mb-0">
                                    <tbody>
                                        <tr v-for="(d, index) in statusData?.last7Days || []" :key="`last7Days-${index}`">
                                            <td class="font-monospace">{{ dateFormat(d.day, 'L') }}</td>
                                            <td class="text-end" :class="`text-${getReportsColorClass(d.count)}`">
                                                {{ d.count }} {{ '/ 24 reports' }}
                                            </td>
                                        </tr>
                                        <tr v-if="!statusData?.last7Days?.length">
                                            <td colspan="2" class="text-center fs-4 py-4">Not reported</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div class="col-md">
                            <h6>Consecutive Hourly Failures (Last 7 Days)</h6>
                            <div class="overflow-auto" style="max-height: 24.5rem">
                                <table class="table table-striped mb-0">
                                    <tbody>
                                        <tr
                                            v-for="(report, index) in statusData?.failuresLast7Days || []"
                                            :key="`failuresLast7Days-${index}`"
                                        >
                                            <td class="font-monospace text-danger">
                                                {{ dateFormat(report.dateTime, 'MM/DD/YYYY hh:mm A') }}
                                            </td>
                                        </tr>
                                        <tr v-if="!statusData?.failuresLast7Days?.length">
                                            <td class="text-center fs-4 py-4">No failures ✅</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div v-if="statusData?.incidents?.length > 0">
                        <hr />
                        <b>Incidents ({{ statusData?.incidents?.length }})</b>
                        <TinyPumperIncidentsList class="mt-3" :incidents="statusData?.incidents" />
                    </div>
                </template>
            </div>
        </div>
    </Modal>
</template>
